import { crc16 } from './protocol'

export const HUB_PROTOCOL_VERSION = 2
export const HUB_FILE_NAMES = ['data.csv', 'channels.csv', 'session.csv', 'calibration.csv', 'events.csv'] as const
export type HubFileId = 1 | 2 | 3 | 4 | 5
export type HubSessionState = 'EMPTY' | 'RECORDING' | 'FINALIZING' | 'READY' | 'ACKED' | 'ERROR'
export type HubCommand = 'READ_FILE' | 'ACK_SESSION' | 'CLEAR_SESSION'

export interface HubInfo {
  protocolVersion: 2
  serialNumber: string
  firmwareVersion?: string
  batteryPercent?: number
  channelCount: 16
  sessionId: string
  sessionToken: number
  sessionState: HubSessionState
  fileCount: number
  totalBytes: number
}

export type HubFrame =
  | { type: 'START'; fileId: number; transferId: number; fileSize: number; startOffset: number; transferBytes: number }
  | { type: 'DATA'; fileId: number; offset: number; data: Uint8Array }
  | { type: 'END'; fileId: number; transferId: number; nextOffset: number; fileCrc32: number; status: number; eof: boolean }
  | { type: 'RESULT'; command: number; transferId: number; sessionToken: number; status: number; sessionStateCode: number }
  | { type: 'ERROR'; command: number; transferId: number; sessionToken: number; errorCode: number; fileId: number; detail: number }

const COMMAND_CODES: Record<HubCommand, number> = { READ_FILE: 1, ACK_SESSION: 2, CLEAR_SESSION: 3 }
const STATES: HubSessionState[] = ['EMPTY', 'RECORDING', 'FINALIZING', 'READY', 'ACKED', 'ERROR']
const ERROR_NAMES = [
  '', 'BUSY', 'NO_DATA', 'BAD_COMMAND', 'BAD_CRC', 'NOT_ACKNOWLEDGED',
  'BAD_FILE_ID', 'BAD_OFFSET', 'SESSION_MISMATCH', 'BAD_STATE', 'STORAGE_ERROR', 'FILE_TOO_LARGE',
]

function u32(value: unknown, field: string, allowZero = true): number {
  if (!Number.isInteger(value) || (value as number) < (allowZero ? 0 : 1) || (value as number) > 0xffffffff) {
    throw new Error(`Некорректное поле INFO: ${field}`)
  }
  return value as number
}

export function decodeHubInfo(text: string): HubInfo {
  let value: unknown
  try {
    value = JSON.parse(text)
  } catch {
    throw new Error('INFO хаба содержит некорректный JSON')
  }
  if (!value || typeof value !== 'object') throw new Error('INFO хаба имеет неверный формат')
  const info = value as Record<string, unknown>
  if (info.protocolVersion !== 2) throw new Error(`Версия протокола хаба ${String(info.protocolVersion)} не поддерживается`)
  if (typeof info.serialNumber !== 'string' || !info.serialNumber.trim()) throw new Error('INFO хаба не содержит serialNumber')
  if (info.channelCount !== 16) throw new Error('Хаб должен объявить 16 каналов')
  if (typeof info.sessionState !== 'string' || !STATES.includes(info.sessionState as HubSessionState)) {
    throw new Error('INFO хаба содержит неизвестное состояние сеанса')
  }
  const empty = info.sessionState === 'EMPTY'
  if (typeof info.sessionId !== 'string' || (!empty && !info.sessionId)) throw new Error('INFO хаба не содержит sessionId')
  const sessionToken = u32(info.sessionToken, 'sessionToken', empty)
  const fileCount = u32(info.fileCount, 'fileCount')
  const totalBytes = u32(info.totalBytes, 'totalBytes')
  if (empty && (info.sessionId || sessionToken || fileCount || totalBytes)) throw new Error('INFO пустого сеанса противоречив')
  if (!empty && fileCount !== 5) throw new Error(`Хаб объявил ${fileCount} файлов вместо пяти`)
  if (info.batteryPercent !== undefined && (!Number.isInteger(info.batteryPercent) || (info.batteryPercent as number) < 0 || (info.batteryPercent as number) > 100)) {
    throw new Error('INFO хаба содержит некорректный заряд батареи')
  }
  return {
    protocolVersion: 2,
    serialNumber: info.serialNumber.trim(),
    firmwareVersion: typeof info.firmwareVersion === 'string' ? info.firmwareVersion : undefined,
    batteryPercent: info.batteryPercent as number | undefined,
    channelCount: 16,
    sessionId: info.sessionId,
    sessionToken,
    sessionState: info.sessionState as HubSessionState,
    fileCount,
    totalBytes,
  }
}

export function encodeHubCommand(command: HubCommand, fileId: number, transferId: number, argument: number): Uint8Array {
  if (!Number.isInteger(transferId) || transferId < 1 || transferId > 0xffffffff) throw new Error('Некорректный transferId')
  u32(argument, 'argument')
  if (command === 'READ_FILE' ? fileId < 1 || fileId > 5 : fileId !== 0) throw new Error('Некорректный fileId')
  const bytes = new Uint8Array(14)
  const view = new DataView(bytes.buffer)
  view.setUint8(0, 0x4e)
  view.setUint8(1, HUB_PROTOCOL_VERSION)
  view.setUint8(2, COMMAND_CODES[command])
  view.setUint8(3, fileId)
  view.setUint32(4, transferId, true)
  view.setUint32(8, argument, true)
  view.setUint16(12, crc16(bytes.subarray(0, 12)), true)
  return bytes
}

export function decodeHubFrame(view: DataView): HubFrame {
  if (view.byteLength < 10) throw new Error('Усечённый BLE-фрейм хаба')
  if (view.getUint8(0) !== 0x4e || view.getUint8(1) !== 2) throw new Error('Сигнатура или версия BLE-фрейма неверна')
  const type = view.getUint8(2)
  if (type !== 2 && view.byteLength !== 20) throw new Error('Длина служебного BLE-фрейма неверна')
  const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength - 2)
  if (crc16(bytes) !== view.getUint16(view.byteLength - 2, true)) throw new Error('Ошибка CRC-16 BLE-фрейма')
  if (type === 1) return {
    type: 'START', fileId: view.getUint8(3), transferId: view.getUint32(4, true),
    fileSize: view.getUint32(8, true), startOffset: view.getUint32(12, true), transferBytes: view.getUint16(16, true),
  }
  if (type === 2) return {
    type: 'DATA', fileId: view.getUint8(3), offset: view.getUint32(4, true),
    data: Uint8Array.from(new Uint8Array(view.buffer, view.byteOffset + 8, view.byteLength - 10)),
  }
  if (type === 3) return {
    type: 'END', fileId: view.getUint8(3), transferId: view.getUint32(4, true),
    nextOffset: view.getUint32(8, true), fileCrc32: view.getUint32(12, true),
    status: view.getUint8(16), eof: (view.getUint8(17) & 1) !== 0,
  }
  if (type === 4) return {
    type: 'RESULT', command: view.getUint8(3), transferId: view.getUint32(4, true),
    sessionToken: view.getUint32(8, true), status: view.getUint8(12), sessionStateCode: view.getUint8(13),
  }
  if (type === 0x7f) return {
    type: 'ERROR', command: view.getUint8(3), transferId: view.getUint32(4, true),
    sessionToken: view.getUint32(8, true), errorCode: view.getUint8(12),
    fileId: view.getUint8(13), detail: view.getUint32(14, true),
  }
  throw new Error(`Неизвестный тип BLE-фрейма хаба: ${type}`)
}

export function hubError(frame: Extract<HubFrame, { type: 'ERROR' }>): Error {
  return new Error(`Хаб вернул ${ERROR_NAMES[frame.errorCode] ?? `ошибку ${frame.errorCode}`} (fileId ${frame.fileId}, detail ${frame.detail})`)
}

export function crc32Update(state: number, bytes: Uint8Array): number {
  let crc = state >>> 0
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0)
  }
  return crc >>> 0
}

export function crc32(bytes: Uint8Array): number {
  return (crc32Update(0xffffffff, bytes) ^ 0xffffffff) >>> 0
}
